import * as React from "react";

export function useComputedStyle<T extends Element, K extends string & keyof CSSStyleDeclaration>(keys: Array<K>) {
  const [computedStyle, set] = React.useState<undefined | Pick<CSSStyleDeclaration, K>>(undefined);

  const ref = React.useCallback(
    (el: null | T) => {
      if (!el) {
        return;
      }

      /*
       * 'computedStyle' is a live(!) reference to the CSSStyleDeclaration
       * of the element.
       */
      const computedStyle = window.getComputedStyle(el);

      const update = () => {
        set((old) => {
          const entries = keys.map((k) => [k, computedStyle[k]] as const);
          if (!old || entries.some(([k, v]) => old[k] !== v)) {
            return Object.fromEntries(entries) as unknown as Pick<CSSStyleDeclaration, K>;
          } else {
            return old;
          }
        });
      };

      const resizeObserver = new ResizeObserver(update);
      resizeObserver.observe(el);

      const mutationObserver = new MutationObserver(update);
      mutationObserver.observe(el, { attributes: true, childList: true });

      /*
       * Trigger an update right away (~ just after the element is mounted).
       */
      update();

      return () => {
        // set(undefined);

        resizeObserver.disconnect();
        mutationObserver.disconnect();
      };
    },
    [...keys],
  );

  return [ref, computedStyle] as const;
}
